import { Money } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Input } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { AmountFields } from '../../../components/AmountFields';
import { EmptyReturnField } from '../../../components/EmptyReturnField';
import { toSupersedeInput, type SupersedeFormValues } from '../../../policies/build-rate-line-input';
import { formatLineKey } from '../../../policies/rate-line-key';
import { supersedeRateLine, type RateLineView } from '../../../rate-cards.api';
import { failureMessageFor } from '../../../policies/rate-line-failure-message';

interface ChangeLineDrawerProps {
  /** The line being changed, or null while closed. Already on screen, so there is nothing to fetch. */
  line: RateLineView | null;
  onClose: () => void;
}

/** A new price for an existing key: the key and basis are shown, not editable. It opens a draft proposal that takes over on the date chosen once approved. */
export function ChangeLineDrawer({ line, onClose }: ChangeLineDrawerProps) {
  const [form] = Form.useForm<SupersedeFormValues>();
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  useEffect(() => {
    if (!line) return;
    form.setFieldsValue({
      amount: Number(Money.fromJSON(line.amount).toDecimalString()),
      currency: line.amount.currency,
      emptyReturnIncluded: line.emptyReturnIncluded ?? undefined,
    });
  }, [line, form]);

  async function change(values: SupersedeFormValues) {
    if (!line) return;
    const created = await submit(() => supersedeRateLine(line.id, toSupersedeInput(values)).then(() => true), failureMessageFor);
    if (!created) return;
    toast.success('Draft proposal created — submit it for approval from the Proposals tab');
    form.resetFields();
    onClose();
  }

  return (
    <Drawer open={line !== null} onClose={onClose} afterOpenChange={clearError} title="Change price" placement="right" size={480} destroyOnClose>
      {line && <p className="mb-5 text-sm text-muted">{formatLineKey(line)}</p>}
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<SupersedeFormValues> form={form} layout="vertical" requiredMark={false} onFinish={change} disabled={submitting}>
        <AmountFields label="New price" isCurrencyLocked />
        {line?.emptyReturnIncluded != null && <EmptyReturnField />}
        <Form.Item name="effectiveDate" label="Takes over on" extra="Must be after today to be approved" rules={[{ required: true, message: 'Choose a date' }]}>
          <DatePicker className="w-full" disabledDate={(day) => !day.isAfter(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Save as draft
        </Button>
      </Form>
    </Drawer>
  );
}
